import classNames from 'classnames';
import type { AfterOnAppear } from 'components/AnimatedFlipped';
import { AnimatedFlipped } from 'components/AnimatedFlipped';
import WidgetAddButton from 'components/superreports/WidgetAddButton';
import type { WidgetColumnChange } from 'components/ui-blocks/widget';
import { WidgetContainer } from 'components/ui-blocks/widget';
import WidgetDropTarget from 'components/ui-blocks/widget/WidgetDropTarget';
import config from 'config';
import { isAgentOnlyWidget } from 'constants/agentReporting';
import { EMPTY_ARRAY } from 'constants/defaults';
import { WIDGET_TYPE_TO_QUERY_FILTER_MAP } from 'constants/filters';
import { HAS_ADDED_WIDGET_TO_REPORT } from 'constants/hints/reports';
import * as widgetConst from 'constants/widgets';
import type { CSSProperties } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Flipper } from 'react-flip-toolkit';
import { connect, useDispatch, useSelector } from 'react-redux';
import { clearHourDraft, createHourDraftFromId } from 'redux/modules/hours.actions';
import type { FetchHoursForParams, RemoveWidgetParams } from 'redux/modules/superreports.actions';
import { duplicateWidget } from 'redux/modules/superreports.actions';
import type {
  ToggleSelectionParams,
  WidgetEntity,
  WidgetMetadataDictionary,
  WidgetReference,
  WidgetWithData,
} from 'redux/modules/superreports.types';
import { changeWidgetType, moveWidget } from 'redux/modules/superreports.types';
import { saveUserProperty } from 'redux/modules/user.actions';
import { getTagFilterMode } from 'redux/selectors/superreports';
import type { Account } from 'types/account';
import type { MappedActions, ThunkDispatch } from 'types/redux';
import type { EntityDataKey } from 'types/reports';
import { fetchInBatchesOf } from 'utils/api';
import type { CostAndDurationSummary } from 'utils/summary';
import {
  areWidgetRowsExpandable,
  DEFAULT_MAX_WIDGET_LIST_WIDTH,
  getRowIdsFromWidget,
} from 'utils/superreports';
import { useViewportWidthBreakpoint, ViewportWidthBreakpoint } from 'utils/viewport';
import widgetAddButtonStyles from './WidgetAddButton.module.scss';
import styles from './WidgetList.module.scss';
import { WidgetListItem } from './WidgetListItem';
import AgentActivityWidget, { AgentActivityOptions } from './widgets/AgentActivityWidget';
import DonutWidgetBody from './widgets/donut/DonutWidgetBody';
import DonutWidgetHeader from './widgets/donut/DonutWidgetHeader';
import DonutWidgetOptions from './widgets/donut/DonutWidgetOptions';
import NoData from './widgets/NoData';
import type { TableColumnChangeParams } from './widgets/table';
import { TableWidgetBody } from './widgets/table';
import TableWidgetHeader from './widgets/table/TableWidgetHeader';
import TableWidgetOptions from './widgets/table/TableWidgetOptions';
import {
  TimelineWidgetBody,
  TimelineWidgetHeader,
  TimelineWidgetOptions,
} from './widgets/timeline';

const scrollToWidgetAfterAppear: AfterOnAppear = (element, _index, decisionData) => {
  if (
    decisionData?.current === true &&
    !element.classList.contains(widgetAddButtonStyles.wrapper)
  ) {
    const parentOffset = (element.offsetParent as HTMLElement).offsetTop;
    window.scrollTo({ top: element.offsetTop + parentOffset - 30, behavior: 'smooth' });
  }
};

const mapActionsToProps = {
  createHourDraftFromId,
  clearHourDraft,
  saveUserProperty,
  duplicateWidget,
  changeWidgetType,
};

type ActionProps = MappedActions<typeof mapActionsToProps>;

// combining two different types in one event handler here..:
export type WidgetListColumnChange = Partial<WidgetColumnChange & TableColumnChangeParams>;

interface OwnProps {
  account: Account;
  billedState: boolean | null;
  data?: Array<any>;
  hasLoadedInitialData?: boolean;
  hideEntries?: boolean;
  isLoading: boolean;
  isLoadingEntities?: Record<EntityDataKey, boolean>;
  minimumWidgetWidth?: number;
  noMaxWidth?: boolean;
  readOnlyEntries?: boolean;
  readOnlyWidgets?: boolean;
  showAddHint?: boolean;
  showBudgetLine?: boolean;
  totals?: CostAndDurationSummary;
  widgetClientListData?: Array<any>;
  widgetHourLineData?: Array<any>;
  widgetMetadata: WidgetMetadataDictionary;
  widgets: Array<WidgetWithData>;
  onAddWidget?: (widgetReference: Partial<WidgetEntity>) => void;
  onColumnChange: (a: WidgetListColumnChange) => void;
  onFetchReportHoursFor: (params: FetchHoursForParams) => void;
  onRemoveWidget?: (params: RemoveWidgetParams) => void;
  onToggleSelection?: (params: ToggleSelectionParams) => void;
}

type Props = ActionProps & OwnProps;

const WidgetList: React.FC<Props> = ({
  widgets = EMPTY_ARRAY,
  onColumnChange,
  onFetchReportHoursFor,
  ...props
}) => {
  const [draggedReference, setDraggedWidget] = useState<WidgetReference | null>(null);
  const scrollToWidget = useRef<WidgetReference | null>(null);
  const dispatch = useDispatch<ThunkDispatch>();
  const currentWidthBreakpoint = useViewportWidthBreakpoint();
  const tagFilterMode = useSelector(getTagFilterMode);

  const containerStyle = useMemo<CSSProperties | undefined>(
    () =>
      !props.noMaxWidth
        ? {
            maxWidth: Math.max(
              DEFAULT_MAX_WIDGET_LIST_WIDTH,
              props.minimumWidgetWidth ?? DEFAULT_MAX_WIDGET_LIST_WIDTH
            ),
          }
        : undefined,
    [props.minimumWidgetWidth, props.noMaxWidth]
  );

  const handleOpenWidgets = () => {
    props.saveUserProperty(HAS_ADDED_WIDGET_TO_REPORT, true);
  };

  const handleSaveUserProperty = (key: string) => {
    props.saveUserProperty(key as any);
  };

  const handleReportTableColumnChange = useCallback(
    (a: TableColumnChangeParams) => {
      onColumnChange(a);
    },
    [onColumnChange]
  );

  const handleBeginDrag = (reference: WidgetReference) => {
    setDraggedWidget(reference);
  };

  const handleEndDrag = () => {
    setDraggedWidget(null);
  };

  const handleExpandTableRows = useCallback(
    (reference: WidgetReference, widget: WidgetWithData, isExpanded: boolean) => {
      if (!onFetchReportHoursFor) return;

      const allRowIds = getRowIdsFromWidget(widget);

      const dispatcher = (rowId: number) =>
        onFetchReportHoursFor({
          id: rowId,
          widgetReference: reference,
          expandRow: !isExpanded,
          filterScope: WIDGET_TYPE_TO_QUERY_FILTER_MAP[widget.widget_type],
        });

      fetchInBatchesOf(5, allRowIds, someRowIds => Promise.all(someRowIds.map(dispatcher)));
    },
    [onFetchReportHoursFor]
  );

  const createHourDraft = useCallback(
    (id: number) => {
      dispatch(createHourDraftFromId(id, { interaction: 'Click Time Entry' }));
    },
    [dispatch]
  );

  const handleMoveWidget = (
    reference: WidgetReference,
    targetSequence: number,
    currentSequence: number
  ) => {
    // If the widget is moved to the same or immediately following sequence, don't do anything
    if (targetSequence === currentSequence || targetSequence === currentSequence + 1) return;

    scrollToWidget.current = reference;
    dispatch(moveWidget({ reference, targetSequence }));
  };

  let consecutiveHalfBlocks = 0;
  const items: Array<JSX.Element> = [];
  if (!props.readOnlyWidgets) {
    items.push(
      <WidgetAddButton
        agentTrackingEnabled={props.account.features.agent_tracking}
        key="add-button"
        flipId="add-button"
        onOpen={handleOpenWidgets}
        onClick={props.onAddWidget}
        isLoading={props.isLoading}
        isHidden={!!draggedReference}
      />
    );
  }

  widgets.forEach((widget, index) => {
    if (
      (!props.account.features.agent_tracking || config.isPublicReport) &&
      ([
        widgetConst.WIDGET_AGENTS_DONUT,
        widgetConst.WIDGET_AGENTS_LINE,
        widgetConst.WIDGET_AGENTS_LIST,
      ].includes(widget.widget_type) ||
        isAgentOnlyWidget(widget.columns.names))
    ) {
      return;
    }
    const reference: number | string | undefined = widget.id || widget._ref;
    if (reference === undefined) {
      consecutiveHalfBlocks = 0;
      return;
    }

    const isLoadingWidget =
      props.hasLoadedInitialData === false ||
      (props.isLoadingEntities && widget.dataEntities
        ? widget.dataEntities.some(entity => props.isLoadingEntities![entity])
        : props.isLoading);

    if (!widgetConst.COMPATIBLE_WIDGETS.includes(widget.widget_type)) {
      consecutiveHalfBlocks = 0;
      items.push(
        <WidgetListItem
          key={reference}
          reference={reference}
          sequence={widget.sequence}
          draggedReference={draggedReference}
        >
          <WidgetContainer
            reference={reference}
            widget={widget}
            readOnly={props.readOnlyWidgets}
            isFlush
            onRemoveWidget={props.onRemoveWidget}
            isLoading={isLoadingWidget}
            onBeginDrag={handleBeginDrag}
            onEndDrag={handleEndDrag}
            onMoveWidget={handleMoveWidget}
          >
            <NoData message="This widget was added using a legacy or beta version of Timely and cannot be displayed correctly." />
          </WidgetContainer>
        </WidgetListItem>
      );

      if (!props.readOnlyWidgets) {
        items.push(
          <WidgetAddButton
            agentTrackingEnabled={props.account.features.agent_tracking}
            key={`${reference}-add-button`}
            flipId={`${reference}-add-button`}
            referenceWidget={widget}
            onOpen={handleOpenWidgets}
            onClick={props.onAddWidget}
            showAddHint={index === 0 && props.showAddHint}
            hintKey={HAS_ADDED_WIDGET_TO_REPORT}
            saveUserProperty={handleSaveUserProperty}
            isLoading={props.isLoading}
            isHidden={!!draggedReference}
          />
        );
      }

      return;
    }

    switch (widget.widget_type) {
      case widgetConst.WIDGET_AGENTS_DONUT:
      case widgetConst.WIDGET_AGENTS_LINE:
      case widgetConst.WIDGET_AGENTS_LIST: {
        consecutiveHalfBlocks = 0;
        items.push(
          <WidgetListItem
            key={reference}
            reference={reference}
            sequence={widget.sequence}
            draggedReference={draggedReference}
          >
            <WidgetContainer
              reference={reference}
              widget={widget}
              title="Agent activity"
              readOnly={props.readOnlyWidgets}
              isFlush
              onRemoveWidget={props.onRemoveWidget}
              onDuplicateWidget={props.duplicateWidget}
              onBeginDrag={handleBeginDrag}
              onEndDrag={handleEndDrag}
              onMoveWidget={handleMoveWidget}
              options={<AgentActivityOptions widget={widget} />}
            >
              <AgentActivityWidget
                widget={widget}
                enabled={!!props.account.features.agent_tracking}
              />
            </WidgetContainer>
          </WidgetListItem>
        );
        if (!props.readOnlyWidgets)
          items.push(
            <WidgetAddButton
              key={`${reference}-add-button`}
              referenceWidget={widget}
              agentTrackingEnabled={props.account.features.agent_tracking}
              onOpen={handleOpenWidgets}
              onClick={props.onAddWidget}
              isLoading={props.isLoading}
              isHidden={!!draggedReference}
            />
          );
        break;
      }

      case widgetConst.WIDGET_HOURS_CUMULATIVE_LINE:
      case widgetConst.WIDGET_HOURS_LINE: {
        consecutiveHalfBlocks = 0;
        const showCumulative = widget.widget_type === widgetConst.WIDGET_HOURS_CUMULATIVE_LINE;
        items.push(
          <WidgetListItem
            key={reference}
            reference={reference}
            sequence={widget.sequence}
            draggedReference={draggedReference}
          >
            <WidgetContainer
              reference={reference}
              widget={widget}
              readOnly={props.readOnlyWidgets}
              isFlush
              onRemoveWidget={props.onRemoveWidget}
              isLoading={isLoadingWidget}
              onBeginDrag={handleBeginDrag}
              onEndDrag={handleEndDrag}
              onMoveWidget={handleMoveWidget}
              onDuplicateWidget={props.duplicateWidget}
              title={
                <TimelineWidgetHeader
                  widget={widget}
                  readOnly={props.readOnlyWidgets}
                  isLoading={props.isLoading}
                  billedState={props.billedState}
                />
              }
              options={<TimelineWidgetOptions widget={widget} features={props.account.features} />}
            >
              <TimelineWidgetBody
                showBudgetLine={props.showBudgetLine}
                showMedian={!!widget.options?.showMedian}
                showCumulative={showCumulative}
                data={widget.data}
                metadata={widget.metadata}
                options={widget.options}
                isLoading={isLoadingWidget}
                billedState={props.billedState}
              />
            </WidgetContainer>
          </WidgetListItem>
        );

        if (!props.readOnlyWidgets) {
          items.push(
            <WidgetAddButton
              agentTrackingEnabled={props.account.features.agent_tracking}
              key={`${reference}-add-button`}
              flipId={`${reference}-add-button`}
              referenceWidget={widget}
              onOpen={handleOpenWidgets}
              onClick={props.onAddWidget}
              showAddHint={index === 0 && props.showAddHint}
              hintKey={HAS_ADDED_WIDGET_TO_REPORT}
              saveUserProperty={handleSaveUserProperty}
              isLoading={props.isLoading}
              isHidden={!!draggedReference}
            />
          );
        }

        break;
      }

      case widgetConst.WIDGET_PROJECTS_DONUT:
      case widgetConst.WIDGET_CLIENTS_DONUT:
      case widgetConst.WIDGET_TEAMS_DONUT:
      case widgetConst.WIDGET_LABELS_DONUT:
      case widgetConst.WIDGET_LINKED_WORK_DONUT:
      case widgetConst.WIDGET_BILLED_VS_UNBILLED_DONUT:
      case widgetConst.WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT: {
        ++consecutiveHalfBlocks;
        const nextWidget = widgets[index + 1];
        const nextWidgetIsHalfBlock = widgetConst.DONUT_WIDGETS.includes(nextWidget?.widget_type);

        const even = consecutiveHalfBlocks % 2 === 0;
        const single =
          (!nextWidgetIsHalfBlock && !even) ||
          currentWidthBreakpoint < ViewportWidthBreakpoint.Wide;
        let total;
        let totalTooltipText;
        if (
          [widgetConst.WIDGET_LABELS_DONUT, widgetConst.WIDGET_TEAMS_DONUT].includes(
            widget.widget_type
          )
        ) {
          total =
            widget.options?.dataType === 'money'
              ? (props.totals?.costLogged ?? 0)
              : (props.totals?.minutesLogged ?? 0) / 60;
          totalTooltipText =
            'Total reflects actual values logged, not the sum of all segments, to avoid duplicates';
        }
        items.push(
          <WidgetListItem
            key={reference}
            reference={reference}
            half
            single={single}
            sequence={widget.sequence}
            draggedReference={draggedReference}
          >
            <WidgetContainer
              reference={reference}
              widget={widget}
              readOnly={props.readOnlyWidgets}
              isFlush
              onRemoveWidget={props.onRemoveWidget}
              isLoading={isLoadingWidget}
              onBeginDrag={handleBeginDrag}
              onEndDrag={handleEndDrag}
              onMoveWidget={handleMoveWidget}
              onDuplicateWidget={props.duplicateWidget}
              options={<DonutWidgetOptions widget={widget} />}
              title={
                <DonutWidgetHeader
                  widget={widget}
                  readOnly={props.readOnlyWidgets}
                  features={props.account.features}
                />
              }
            >
              <DonutWidgetBody
                widgetType={widget.widget_type}
                data={widget.data}
                options={widget.options}
                columns={widget.columns.names}
                isLoading={isLoadingWidget}
                billedState={props.billedState}
                tagFilterMode={tagFilterMode}
                thresholdFactor={widget.threshold_factor}
                total={total}
                totalTooltipText={totalTooltipText}
              />
            </WidgetContainer>
          </WidgetListItem>
        );

        if (!props.readOnlyWidgets && (even || single)) {
          items.push(
            <WidgetAddButton
              agentTrackingEnabled={props.account.features.agent_tracking}
              key={`${reference}-add-button`}
              flipId={`${reference}-add-button`}
              referenceWidget={widget}
              onOpen={handleOpenWidgets}
              onClick={props.onAddWidget}
              showAddHint={index === 0 && props.showAddHint}
              hintKey={HAS_ADDED_WIDGET_TO_REPORT}
              saveUserProperty={handleSaveUserProperty}
              isLoading={props.isLoading}
              isHidden={!!draggedReference}
            >
              {draggedReference && (
                <WidgetDropTarget targetSequence={widget.sequence + 1} position="add" />
              )}
            </WidgetAddButton>
          );
        }

        break;
      }

      default: {
        consecutiveHalfBlocks = 0;
        const isEmpty = !widget.rows?.length;
        const areRowsExpandable = areWidgetRowsExpandable(widget.widget_type, props.hideEntries);
        const canExpandRows = areRowsExpandable && !isEmpty;

        items.push(
          <WidgetListItem
            key={reference}
            reference={reference}
            sequence={widget.sequence}
            draggedReference={draggedReference}
          >
            <WidgetContainer
              reference={reference}
              widget={widget}
              readOnly={props.readOnlyWidgets}
              isFlush
              onRemoveWidget={props.onRemoveWidget}
              isLoading={isLoadingWidget}
              canExpandRows={canExpandRows}
              onToggleExpandRows={expanded => handleExpandTableRows(reference, widget, expanded)}
              onDuplicateWidget={props.duplicateWidget}
              onBeginDrag={handleBeginDrag}
              onEndDrag={handleEndDrag}
              onMoveWidget={handleMoveWidget}
              title={
                <TableWidgetHeader
                  widget={widget}
                  readOnly={props.readOnlyWidgets}
                  changeWidgetType={props.changeWidgetType}
                />
              }
              options={
                <TableWidgetOptions
                  widget={widget}
                  billedState={props.billedState}
                  features={props.account.features}
                />
              }
            >
              <TableWidgetBody
                readOnly={props.readOnlyEntries || props.readOnlyWidgets}
                widget={widget}
                onColumnChange={handleReportTableColumnChange}
                isLoading={isLoadingWidget}
                billedState={props.billedState}
                tagFilterMode={tagFilterMode}
                totals={props.totals}
                currency={props.account.currency}
                onFetchReportHoursFor={onFetchReportHoursFor}
                onCreateHourDraft={createHourDraft}
                onToggleSelection={props.onToggleSelection}
                entityMetadata={props.widgetMetadata}
                hideEntries={props.hideEntries}
                features={props.account.features}
              />
            </WidgetContainer>
          </WidgetListItem>
        );

        if (!props.readOnlyWidgets) {
          items.push(
            <WidgetAddButton
              agentTrackingEnabled={props.account.features.agent_tracking}
              key={`${reference}-add-button`}
              flipId={`${reference}-add-button`}
              referenceWidget={widget}
              onOpen={handleOpenWidgets}
              onClick={props.onAddWidget}
              showAddHint={index === 0 && props.showAddHint}
              hintKey={HAS_ADDED_WIDGET_TO_REPORT}
              saveUserProperty={handleSaveUserProperty}
              isLoading={props.isLoading}
              isHidden={!!draggedReference}
            />
          );
        }

        break;
      }
    }
  });

  if (props.readOnlyWidgets) {
    return (
      <ul
        className={classNames(styles.widgetList, {
          [styles.lockMaxWidth]: !!props.minimumWidgetWidth,
        })}
        style={containerStyle}
      >
        {items}
      </ul>
    );
  }

  return (
    <div className={styles.widgetListWrapper} style={containerStyle}>
      <Flipper
        className={classNames(styles.widgetList, {
          [styles.isDragging]: draggedReference,
          [styles.lockMaxWidth]: !!props.minimumWidgetWidth,
        })}
        element="ul"
        flipKey={items.map(i => i.key).join('')}
        decisionData={props.hasLoadedInitialData}
      >
        {items.map(item => (
          <AnimatedFlipped
            key={item.key}
            flipId={item.key!}
            transformOrigin="50% 0"
            afterOnAppear={scrollToWidgetAfterAppear}
          >
            {item}
          </AnimatedFlipped>
        ))}
      </Flipper>
    </div>
  );
};

export default connect(null, mapActionsToProps)(WidgetList);
